import React, {useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo} from 'react';
import {createPortal} from 'react-dom';
import {RangeKeyDict, RangeFocus} from 'react-date-range';
import {subMonths, addMonths, startOfDay, startOfMonth} from 'date-fns';
import {useSelector} from 'react-redux';
import * as reduxSelectors from 'components/store/application.reducers';
import {KEY} from 'components/common/constants';
import 'components/common/calendar/theme.scss';
import {YearGridState, MonthGridState} from 'components/common/calendar/CalendarNavigator.component';
import {YearGridPanel} from 'components/common/calendar/YearGridPanel.component';
import {MonthGridPanel} from 'components/common/calendar/MonthGridPanel.component';
import {CalendarPickerPanel} from 'components/common/calendar/CalendarPickerPanel.component';
import {CalendarFooter, RangeEnd} from 'components/common/calendar/CalendarFooter.component';
import {CalendarMaskedInput} from 'components/common/calendar/CalendarMaskedInput.component';
import {
    CalendarQuickSelect,
    DateRange,
    QuickOption,
    matchQuickOption
} from 'components/common/calendar/CalendarQuickSelect.component';
import {
    getDateFnsLocale,
    buildRange,
    sameOrAfter,
    sameMonth,
    sameDay,
    usePopoverClose,
    DIGITS_PER_DATE,
    buildMaskedValue,
    completionDigits,
    dateToDigits,
    extractDigits,
    maskTemplate,
    maxMaskDigits,
    resolveTypedDate,
    focusableWithin,
    focusNextTabbable,
} from 'components/common/calendar/calendarHelpers';

interface NewCalendarProps {
    value?: string;
    /** Overrides the dd-mm-yyyy mask shown while the field is empty. */
    placeholder?: string;
    onChange?: (range: DateRange) => void;
    initialStartDate?: Date;
    initialEndDate?: Date;
    /** Extra arguments fill the `%1`-style placeholders of the translated string. */
    translate?: (key: string, ...args: string[]) => string;
    disabled?: boolean;
    disablePastDates?: boolean;
    /** Earliest selectable date. Takes precedence over disablePastDates when set. */
    minDate?: Date;
    id?: string;
    showQuickSelect?: boolean;
    /** When true only one calendar month is shown (single-panel mode). Default: false */
    singlePanel?: boolean;
    /**
     * Range fields start empty so the mask shows through. Set this when the field
     * already carries a value — a filter that is switched on, say — and the initial
     * dates should read as its value rather than as a suggestion.
     */
    prefilled?: boolean;
    /**
     * Empties the field. Only consumers that pass this get a clear button, and it is
     * up to them to drop whatever the value stood for.
     */
    onClear?: () => void;
    /** Extra class names applied to the calendar input. */
    fieldClassName?: string;
    /** Explicit tab position, for forms that order their fields by hand. */
    tabIndex?: number;
}

export const NewCalendar: React.FC<NewCalendarProps> = ({
                                                            value,
                                                            placeholder,
                                                            onChange,
                                                            initialStartDate = new Date(),
                                                            initialEndDate = new Date(),
                                                            translate,
                                                            disabled = false,
                                                            disablePastDates = false,
                                                            minDate: minDateProp,
                                                            id,
                                                            showQuickSelect = false,
                                                            singlePanel = false,
                                                            prefilled = false,
                                                            onClear,
                                                            fieldClassName,
                                                            tabIndex,
                                                        }) => {
    // Single-panel fields hold one date, dual-panel fields a range.
    const isRange = !singlePanel;
    // A single-date field always carries a real value (its consumers prefill it), so
    // it starts filled in. A range field starts empty so the mask shows through, unless
    // the consumer says its initial dates are already the field's value.
    const initialDigits = singlePanel
        ? dateToDigits(initialStartDate)
        : (prefilled ? dateToDigits(initialStartDate) + dateToDigits(initialEndDate) : '');

    const [isOpen, setIsOpen] = useState(false);
    const [appliedRange, setAppliedRange] = useState(() => buildRange(initialStartDate, initialEndDate));
    const [pendingRange, setPendingRange] = useState(() => buildRange(initialStartDate, initialEndDate));
    // Digits entered in the field, 'ddmmyyyy' per date. `applied` is what the field
    // falls back to when the popup is cancelled.
    const [typedDigits, setTypedDigits] = useState(initialDigits);
    const [appliedDigits, setAppliedDigits] = useState(initialDigits);
    const [focusedRange, setFocusedRange] = useState<RangeFocus>([0, 0]);
    const [sharedPreview, setSharedPreview] = useState<{ startDate: Date; endDate: Date; color: string } | null>(null);
    const [showLeftDate, setShowLeftDate] = useState<Date>(() => new Date());
    const [showRightDate, setShowRightDate] = useState<Date>(() => addMonths(new Date(), 1));
    const [calendarLeftKey, setCalendarLeftKey] = useState(0);
    const [calendarRightKey, setCalendarRightKey] = useState(0);
    const [yearGrid, setYearGrid] = useState<YearGridState | null>(null);
    const [monthGrid, setMonthGrid] = useState<MonthGridState | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
    const yearGridRef = useRef<HTMLDivElement>(null);
    const monthGridRef = useRef<HTMLDivElement>(null);
    // Refs so makeShownDateChangeHandler (empty-dep useCallback) can read current values
    const showLeftDateRef = useRef(showLeftDate);
    const showRightDateRef = useRef(showRightDate);
    showLeftDateRef.current = showLeftDate;
    showRightDateRef.current = showRightDate;
    // Mirrors isOpen synchronously: openModal focuses the field, which fires onFocus
    // and would otherwise re-enter openModal before the state update lands.
    const isOpenRef = useRef(false);
    // Set while focus is handed back to the field on purpose, so the onFocus that
    // follows does not immediately reopen the popup that was just closed.
    const skipReopenRef = useRef(false);

    usePopoverClose(!!yearGrid, yearGridRef, () => setYearGrid(null));
    usePopoverClose(!!monthGrid, monthGridRef, () => setMonthGrid(null));

    // Closing a grid unmounts whatever holds focus, so it goes back to the button the
    // grid was opened from.
    const closeYearGrid = () => {
        yearGrid?.trigger?.focus();
        setYearGrid(null);
    };
    const closeMonthGrid = () => {
        monthGrid?.trigger?.focus();
        setMonthGrid(null);
    };

    // Picking a month or year rebuilds the panel, so the button that opened the grid
    // is gone by the time focus can go back to it. The replacement is looked up once
    // the new panel is on screen.
    const refocusAfterRender = useRef<(() => void) | null>(null);
    useEffect(() => {
        const restore = refocusAfterRender.current;
        if (!restore) return;
        refocusAfterRender.current = null;
        restore();
    });

    const focusPickerButton = (panelIdx: 0 | 1, which: 'Month' | 'Year') => {
        const panel = popupRef.current?.querySelectorAll('.rdrMonthAndYearWrapper')[panelIdx];
        panel?.querySelector<HTMLElement>(`.rdr${which}Picker .rdr-picker-btn`)?.focus();
    };

    // ─── Language, locale and bounds ──────────────────────────────────────────

    // Use the active app language (state.translations.language) so the calendar
    // follows Ctrl+Alt+L language switches. Falls back to the user's profile
    // language if the active one isn't set yet (app boot).
    const appLanguage = useSelector(reduxSelectors.getLanguage);
    const userLanguage = useSelector(reduxSelectors.getUsersCurrentLanguage);
    const langCode = appLanguage ?? userLanguage ?? 'en';
    const locale = useMemo(() => getDateFnsLocale(langCode), [langCode]);
    // Anchor typed dates are resolved against. Memoised so it can be a hook dep.
    const today = useMemo(() => startOfDay(new Date()), []);
    const minDate = minDateProp ?? (disablePastDates ? today : undefined);
    const minTime = minDate?.getTime();
    // Where an emptied field starts over from: today, or the earliest selectable day
    // when that is later.
    const neutralDate = minDate && minDate > today ? startOfDay(minDate) : today;

    const t = (key: string, fallback: string, ...args: string[]) => translate?.(key, ...args) ?? fallback;

    // The format quoted in the field's accessible name is the mask itself, so what a
    // screen reader describes cannot drift from the separator the field really uses.
    const fieldMask = maskTemplate(isRange);

    // ─── Typed value ──────────────────────────────────────────────────────────

    const maxDigits = maxMaskDigits(isRange);
    const startDigits = typedDigits.slice(0, DIGITS_PER_DATE);
    const endDigits = typedDigits.slice(DIGITS_PER_DATE);

    // Resolving scans day by day, so keep it off the render path of unrelated
    // updates (hovering a day in the calendar re-renders on every mouse move).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const typedStart = useMemo(() => resolveTypedDate(startDigits, today, minDate), [startDigits, today, minTime]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const typedEnd = useMemo(() => resolveTypedDate(endDigits, today, minDate), [endDigits, today, minTime]);

    const masked = buildMaskedValue(
        typedDigits,
        [
            // With nothing typed a range field suggests nothing for the start date
            // (the bare dd-mm-yyyy mask shows), while the end date is prefilled with
            // the current selection — today for a fresh field.
            completionDigits(typedStart ?? (isRange ? null : pendingRange.startDate), startDigits),
            completionDigits(typedEnd ?? pendingRange.endDate, endDigits),
        ],
        isRange,
    );

    // A consumer-supplied placeholder replaces the mask, and is left to the native
    // placeholder attribute so the two never draw on top of each other.
    const usePlaceholderAttr = !!placeholder && value === undefined && typedDigits === '';
    const typedText = value ?? masked.typed;
    const ghostText = value !== undefined || usePlaceholderAttr ? '' : masked.ghost;

    // ─── Popup plumbing ───────────────────────────────────────────────────────

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (yearGridRef.current?.contains(target)) return;
            if (monthGridRef.current?.contains(target)) return;
            // Popup is portaled to <body>, so it is outside containerRef — treat
            // clicks inside it as inside.
            if (popupRef.current?.contains(target)) return;
            if (containerRef.current && !containerRef.current.contains(target)) cancelDates();
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === KEY.ESCAPE) {
                if (yearGrid) closeYearGrid();
                else if (monthGrid) closeMonthGrid();
                else cancelDates();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, yearGrid, monthGrid, appliedRange, appliedDigits]);

    // Position the portaled popup relative to the trigger. Runs before paint so
    // the popup never flashes at the wrong spot. Keeps it inside the viewport and
    // flips above the trigger when there isn't enough room below.
    useLayoutEffect(() => {
        if (!isOpen) {
            setPopupPos(null);
            return;
        }
        const updatePosition = () => {
            const trigger = containerRef.current;
            const popup = popupRef.current;
            if (!trigger || !popup) return;
            const rect = trigger.getBoundingClientRect();
            const popupW = popup.offsetWidth;
            const popupH = popup.offsetHeight;
            const margin = 4;

            let top = rect.bottom + margin;
            if (top + popupH > window.innerHeight && rect.top - margin - popupH >= 0) {
                top = rect.top - margin - popupH;
            }
            let left = rect.left;
            if (left + popupW > window.innerWidth - margin) {
                left = Math.max(margin, window.innerWidth - margin - popupW);
            }
            setPopupPos({top, left});
        };
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen]);

    /** Bring the months holding `start` / `end` into view. */
    const showMonthsFor = (start: Date, end: Date) => {
        const startMonth = startOfMonth(start);
        setShowLeftDate(startMonth);
        setCalendarLeftKey(k => k + 1);
        if (singlePanel) return;
        const endMonth = startOfMonth(end);
        setShowRightDate(endMonth > startMonth ? endMonth : addMonths(startMonth, 1));
        setCalendarRightKey(k => k + 1);
    };

    // ─── Focus ────────────────────────────────────────────────────────────────

    /** Hand focus back to the field without the onFocus reopening the popup. */
    const refocusField = () => {
        skipReopenRef.current = true;
        inputRef.current?.focus();
        // focus() fires onFocus synchronously, so the guard is spent by now.
        skipReopenRef.current = false;
    };

    const isInsideCalendar = (node: Node | null) => !!node && (
        !!popupRef.current?.contains(node)
        || !!yearGridRef.current?.contains(node)
        || !!monthGridRef.current?.contains(node)
    );

    const closePopup = () => {
        isOpenRef.current = false;
        setIsOpen(false);
        // Whatever held focus is about to be unmounted, so bring it back to the field
        // rather than letting it fall to the top of the page.
        if (isInsideCalendar(document.activeElement)) refocusField();
    };

    /**
     * Focus leaving the calendar takes the popup with it. Clicking elsewhere is
     * already caught by the mousedown listener, but focus also moves on its own —
     * tabbing on to the next field, a programmatic focus, a screen reader jumping
     * away — and an open popup must not be left behind.
     */
    const handleFocusOut = (event: React.FocusEvent<HTMLDivElement>) => {
        if (!isOpenRef.current) return;
        const next = event.relatedTarget as Node | null;
        // Moving between the field, the popup and the month/year grids stays inside.
        if (next && (containerRef.current?.contains(next) || isInsideCalendar(next))) return;
        // Losing the whole window is not leaving the field: the popup should still be
        // there when the user comes back to the tab.
        if (!next && !document.hasFocus()) return;
        cancelDates();
    };

    const openModal = () => {
        if (disabled || isOpenRef.current || skipReopenRef.current) return;
        isOpenRef.current = true;

        setPendingRange(appliedRange);
        setTypedDigits(appliedDigits);
        setFocusedRange([0, 0]);
        setSharedPreview(null);
        showMonthsFor(appliedRange.startDate, appliedRange.endDate);

        setIsOpen(true);
        // Clicking the calendar button should leave the field ready to type in.
        inputRef.current?.focus();
    };

    const toggleModal = () => {
        if (disabled) return;
        if (isOpenRef.current) cancelDates();
        else openModal();
    };

    /** Make a range the field's value and close the popup. */
    const commitRange = (start: Date, end: Date) => {
        const [from, to] = start <= end ? [start, end] : [end, start];
        const range = buildRange(from, to);
        const digits = dateToDigits(from) + (isRange ? dateToDigits(to) : '');

        setAppliedRange(range);
        setPendingRange(range);
        setTypedDigits(digits);
        setAppliedDigits(digits);
        onChange?.({startDate: from, endDate: to});
        closePopup();
    };

    const applyDates = () => commitRange(pendingRange.startDate, pendingRange.endDate);

    const cancelDates = () => {
        setPendingRange(appliedRange);
        setTypedDigits(appliedDigits);
        closePopup();
    };

    /**
     * Back to an empty field. The stored range goes back to a neutral default along
     * with the digits: a cleared field must not keep the old selection around for the
     * next open to re-apply, or for the ghost text to go on suggesting its end date.
     * What the value stood for is the consumer's to drop.
     */
    const clearDates = () => {
        const neutral = buildRange(neutralDate, neutralDate);
        setTypedDigits('');
        setAppliedDigits('');
        setAppliedRange(neutral);
        setPendingRange(neutral);
        setFocusedRange([0, 0]);
        setSharedPreview(null);
        showMonthsFor(neutralDate, neutralDate);
        closePopup();
        onClear?.();
    };

    // ─── Typing in the field ──────────────────────────────────────────────────

    /**
     * The field only ever holds digits: every keystroke, paste and backspace is
     * reduced to them, and the mask is rebuilt from there.
     */
    const handleTypedChange = (raw: string) => {
        if (disabled) return;
        const digits = extractDigits(raw).slice(0, maxDigits);
        if (digits === typedDigits) return;

        openModal(); // typing into a closed field opens the picker; a no-op when open
        setTypedDigits(digits);

        const start = resolveTypedDate(digits.slice(0, DIGITS_PER_DATE), today, minDate);
        const end = isRange ? resolveTypedDate(digits.slice(DIGITS_PER_DATE), today, minDate) : start;
        // Once the start date is complete, the next click in the calendar should set
        // the end date rather than start over.
        if (isRange) setFocusedRange([0, digits.length >= DIGITS_PER_DATE ? 1 : 0]);
        if (!start && !end) return;

        const nextStart = start ?? pendingRange.startDate;
        const nextEnd = (isRange ? end : start) ?? pendingRange.endDate;
        setPendingRange(buildRange(nextStart, nextEnd));

        // Follow whichever date is being typed with the visible month.
        const editingEnd = isRange && digits.length > DIGITS_PER_DATE;
        const shown = editingEnd ? showRightDate : showLeftDate;
        if (!sameMonth(editingEnd ? nextEnd : nextStart, shown)) showMonthsFor(nextStart, nextEnd);
    };

    /** Nothing has been typed or picked since the popup opened. */
    const isUntouched = () => typedDigits === appliedDigits
        && sameDay(pendingRange.startDate, appliedRange.startDate)
        && sameDay(pendingRange.endDate, appliedRange.endDate);

    /**
     * Tab and Enter accept the ghost text: the suggested dates become the value.
     * An untouched field is left alone, so tabbing through a filter never applies
     * one by accident.
     */
    const acceptSuggestion = () => {
        if (isUntouched()) {
            closePopup();
            return;
        }
        const start = typedStart ?? pendingRange.startDate;
        commitRange(start, (isRange ? typedEnd : typedStart) ?? pendingRange.endDate);
    };

    /** Move focus onto the first control of the open popup. */
    const focusPopup = () => {
        const [first] = focusableWithin(popupRef.current);
        first?.focus();
        return !!first;
    };

    /** Leave the calendar forwards: drop the popup and carry on down the page. */
    const tabOutOfPopup = () => {
        const container = containerRef.current;
        const portals = [popupRef.current, yearGridRef.current, monthGridRef.current];
        // Closing puts focus back in the field, which is where it stays if there is
        // nothing further down the page to move on to.
        cancelDates();
        focusNextTabbable(container, portals);
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;
        if (event.key === KEY.ENTER) {
            event.preventDefault();
            acceptSuggestion();
            return;
        }
        if (event.key === KEY.TAB) {
            // Going backwards means the user is leaving the field: let the popup go
            // and follow the page's own order.
            if (event.shiftKey) {
                if (isOpenRef.current) cancelDates();
                return;
            }
            // The popup is portaled to <body>, so Tab would skip straight past it.
            // A field that has been typed into is finished with Tab as it always was;
            // an untouched one hands over to the picker instead, so the month, year
            // and day controls can be reached from the keyboard.
            if (isOpenRef.current && isUntouched() && focusPopup()) {
                event.preventDefault();
                return;
            }
            // No preventDefault: focus carries on to the next field.
            acceptSuggestion();
            return;
        }
        if (event.key === KEY.ARROW_DOWN) {
            event.preventDefault();
            if (isOpenRef.current) focusPopup();
            else openModal();
        }
    };

    /**
     * Tab inside the popup runs between the field and the page around it: back past
     * the first control returns to the field, forward past the last one closes the
     * popup and moves on to whatever follows the calendar.
     */
    const handlePopupKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== KEY.TAB) return;
        const items = focusableWithin(popupRef.current);
        if (!items.length) return;

        if (event.shiftKey && document.activeElement === items[0]) {
            event.preventDefault();
            refocusField();
        } else if (!event.shiftKey && document.activeElement === items[items.length - 1]) {
            event.preventDefault();
            tabOutOfPopup();
        }
    };

    // ─── Picking in the calendar ──────────────────────────────────────────────

    const handleChange = (rangesByKey: RangeKeyDict) => {
        const {startDate, endDate} = rangesByKey.date;
        if (singlePanel) {
            // Single-date mode: the first (and only) click picks one date.
            // focusedRange starts at [0,0] so startDate is always the clicked date.
            const selected = startDate!;
            commitRange(selected, selected);
            return;
        }
        setPendingRange(prev => ({...prev, startDate, endDate}));
        // Picking in the calendar writes back into the field, also when only the end
        // date moved.
        if (startDate && endDate) setTypedDigits(dateToDigits(startDate) + dateToDigits(endDate));
    };

    /**
     * A date typed into one of the footer fields. Whichever end is being typed pushes
     * the other one along rather than turning the range inside out, and the field the
     * popup hangs from follows, as it does when a day is picked in the calendar.
     */
    const handleFooterDateTyped = (which: RangeEnd, date: Date) => {
        const start = which === 'start' ? date : (date < pendingRange.startDate ? date : pendingRange.startDate);
        const end = which === 'end' ? date : (date > pendingRange.endDate ? date : pendingRange.endDate);
        setPendingRange(buildRange(start, end));
        setTypedDigits(dateToDigits(start) + dateToDigits(end));
        // Follow the typed date with the panel that is meant to show it.
        if (!sameMonth(date, which === 'start' ? showLeftDate : showRightDate)) showMonthsFor(start, end);
    };

    const applyQuickOption = (opt: QuickOption) => {
        const range = opt.getRange();
        setPendingRange(buildRange(range.startDate, range.endDate));
        setTypedDigits(dateToDigits(range.startDate) + dateToDigits(range.endDate));
    };

    const handleYearSelect = (year: number) => {
        if (!yearGrid) return;
        const newDate = new Date(year, yearGrid.month, 1);
        if (yearGrid.monthIdx === 0) {
            setShowLeftDate(newDate);
            setCalendarLeftKey(k => k + 1);
            if (sameOrAfter(newDate, showRightDate)) {
                setShowRightDate(addMonths(newDate, 1));
                setCalendarRightKey(k => k + 1);
            }
        } else {
            setShowRightDate(newDate);
            setCalendarRightKey(k => k + 1);
            if (sameOrAfter(showLeftDate, newDate)) {
                setShowLeftDate(subMonths(newDate, 1));
                setCalendarLeftKey(k => k + 1);
            }
        }
        const {monthIdx} = yearGrid;
        refocusAfterRender.current = () => focusPickerButton(monthIdx, 'Year');
        setYearGrid(null);
    };

    const handleMonthSelect = (month: number, year: number) => {
        if (!monthGrid) return;
        const newDate = new Date(year, month, 1);
        if (monthGrid.panelIdx === 0) {
            setShowLeftDate(newDate);
            setCalendarLeftKey(k => k + 1);
            if (sameOrAfter(newDate, showRightDate)) {
                setShowRightDate(addMonths(newDate, 1));
                setCalendarRightKey(k => k + 1);
            }
        } else {
            setShowRightDate(newDate);
            setCalendarRightKey(k => k + 1);
            if (sameOrAfter(showLeftDate, newDate)) {
                setShowLeftDate(subMonths(newDate, 1));
                setCalendarLeftKey(k => k + 1);
            }
        }
        const {panelIdx} = monthGrid;
        refocusAfterRender.current = () => focusPickerButton(panelIdx, 'Month');
        setMonthGrid(null);
    };

    const makeShownDateChangeHandler = useCallback((panelIdx: 0 | 1) => (date: Date) => {
        if (panelIdx === 0) {
            setShowLeftDate(date);
            if (!singlePanel && sameOrAfter(date, showRightDateRef.current)) {
                setShowRightDate(addMonths(date, 1));
                setCalendarRightKey(k => k + 1);
            }
        } else {
            setShowRightDate(date);
            if (sameOrAfter(showLeftDateRef.current, date)) {
                setShowLeftDate(subMonths(date, 1));
                setCalendarLeftKey(k => k + 1);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singlePanel]);

    const activeQuickIdx = matchQuickOption(pendingRange.startDate, pendingRange.endDate);

    const stableRangeObj = useRef({startDate: pendingRange.startDate, endDate: pendingRange.endDate, key: 'date'});
    const stableRanges = useRef([stableRangeObj.current]);
    stableRangeObj.current.startDate = pendingRange.startDate;
    stableRangeObj.current.endDate = pendingRange.endDate;

    const handlePreviewChange = useCallback((value: Date | null) => {
        if (!value) {
            setSharedPreview(null);
            return;
        }
        if (focusedRange[1] === 1) {
            let start = stableRangeObj.current.startDate;
            let end = value;
            if (end < start) [start, end] = [end, start];
            setSharedPreview({startDate: start, endDate: end, color: 'var(--ga-color-primary)'});
        } else {
            setSharedPreview({startDate: value, endDate: value, color: 'var(--ga-color-primary)'});
        }
    }, [focusedRange]);

    return (
        // onBlur bubbles like focusout, and portalled children still reach it through
        // the React tree, so this one handler covers the field, the popup and the grids.
        <div ref={containerRef} className="new-calendar-wrapper" onBlur={handleFocusOut}>
            <CalendarMaskedInput
                id={id ?? 'new-calendar-input'}
                typed={typedText}
                ghost={ghostText}
                {...(usePlaceholderAttr && {placeholder})}
                disabled={disabled}
                isOpen={isOpen}
                ariaLabel={isRange
                    ? t('calendar.dateRangeInput', `Date range, format ${fieldMask}`, fieldMask)
                    : t('calendar.dateInput', `Date, format ${fieldMask}`, fieldMask)}
                toggleLabel={t('calendar.calendar', 'Open calendar')}
                {...(fieldClassName && {className: fieldClassName})}
                {...(tabIndex !== undefined && {tabIndex})}
                inputRef={inputRef}
                onValueChange={handleTypedChange}
                onKeyDown={handleInputKeyDown}
                onOpen={openModal}
                onToggle={toggleModal}
                // Nothing in the field, nothing to clear.
                {...(onClear && typedText !== '' && {
                    onClear: clearDates,
                    clearLabel: t('calendar.clearDate', 'Clear date')
                })}
            />

            {isOpen && createPortal(
                <div
                    ref={popupRef}
                    className={`calendar-popup${singlePanel ? ' calendar-popup--single' : ''}`}
                    onKeyDown={handlePopupKeyDown}
                    style={{
                        position: 'fixed',
                        top: popupPos?.top ?? 0,
                        left: popupPos?.left ?? 0,
                        visibility: popupPos ? 'visible' : 'hidden',
                    }}
                >
                    <div className="calendar-popup-body">
                        {showQuickSelect && (
                            <CalendarQuickSelect
                                activeQuickIdx={activeQuickIdx}
                                onSelect={applyQuickOption}
                                translate={translate}
                            />
                        )}

                        <CalendarPickerPanel
                            singlePanel={singlePanel}
                            calendarLeftKey={calendarLeftKey}
                            showLeftDate={showLeftDate}
                            onShownDateChange0={makeShownDateChangeHandler(0)}
                            calendarRightKey={calendarRightKey}
                            showRightDate={showRightDate}
                            onShownDateChange1={makeShownDateChangeHandler(1)}
                            ranges={stableRanges.current}
                            onChange={handleChange}
                            locale={locale}
                            focusedRange={focusedRange}
                            onRangeFocusChange={setFocusedRange}
                            preview={sharedPreview ?? undefined}
                            onPreviewChange={handlePreviewChange}
                            {...(minDate && {minDate})}
                            yearGrid={yearGrid}
                            monthGrid={monthGrid}
                            setYearGrid={setYearGrid}
                            setMonthGrid={setMonthGrid}
                        />
                    </div>

                    {!singlePanel &&
                        <CalendarFooter
                            startDate={pendingRange.startDate}
                            endDate={pendingRange.endDate}
                            onApply={applyDates}
                            onCancel={cancelDates}
                            resolveDate={digits => resolveTypedDate(digits, today, minDate)}
                            onDateTyped={handleFooterDateTyped}
                            translate={translate}
                        />
                    }
                </div>,
                document.body
            )}

            {yearGrid && createPortal(
                <YearGridPanel ref={yearGridRef} selectedYear={yearGrid.year} onSelect={handleYearSelect}
                               style={{top: yearGrid.top, left: yearGrid.left}}/>,
                document.body
            )}
            {monthGrid && createPortal(
                <MonthGridPanel ref={monthGridRef} selectedMonth={monthGrid.month} selectedYear={monthGrid.year}
                                onSelect={handleMonthSelect} style={{top: monthGrid.top, left: monthGrid.left}}
                                locale={locale}/>,
                document.body
            )}
        </div>
    );
};

export default NewCalendar;
